import { DialogComponent } from '@theme/dialog';
import '@theme/drag-zoom-wrapper';

/** Browse colour photos without mutating the PDP's purchase selection. */
class AtelierColourPreview extends DialogComponent {
  #root; #opener; #chips; #listeners; #index = 0; #image; #swipe;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#root = this.closest('fivepoint-pdp');
    this.#opener = this.#root.querySelector('[data-colour-preview-open]');
    this.#chips = [...this.#root.querySelectorAll('.fp-chips [data-fp-chip]:not(.fp-chip--other)')];
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#opener.addEventListener('click', this.#open, options);
    this.#root.addEventListener('atelier:selection', this.#syncOpener, options);
    this.addEventListener('dialog:open', this.#onOpen, options);
    this.refs.dialog.addEventListener('close', this.#onClose, options);
    this.addEventListener('click', this.#onClick, options);
    this.addEventListener('keydown', this.#onKeyDown, options);
    const stage = this.#element('image');
    stage.addEventListener('touchstart', this.#touchStart, { ...options, passive: true });
    stage.addEventListener('touchend', this.#touchEnd, { ...options, passive: true });
    stage.addEventListener('touchcancel', this.#cancelSwipe, options);
    this.#syncOpener();
  }

  #element(name) { return this.querySelector(`[data-colour-preview-${name}]`); }
  #selected() { return this.#root.querySelector('.fp-chips [aria-checked="true"]'); }
  #name(chip) {
    return chip.querySelector('u')?.textContent.trim() || '';
  }
  #available(chip) { return !chip.disabled && chip.getAttribute('aria-disabled') !== 'true'; }

  #syncOpener = () => {
    const selected = this.#selected();
    const other = selected?.classList.contains('fp-chip--other');
    const src = selected?.querySelector('img')?.dataset.atelierFullSrc;
    this.#opener.hidden = !!other;
    this.#opener.disabled = !src;
    this.#root.querySelector('[data-colour-preview-missing]').hidden = !selected || !!other || !!src;
  };

  #open = () => {
    this.#index = this.#chips.indexOf(this.#selected());
    if (this.#index < 0) return;
    this.showDialog();
  };

  #onOpen = () => {
    const strip = this.#element('strip');
    strip.replaceChildren();
    this.#chips.forEach((chip, index) => {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'fp-colour-preview__thumb';
      button.dataset.colourPreviewIndex = String(index);
      button.setAttribute('aria-label', this.#name(chip));
      button.toggleAttribute('data-unavailable', !this.#available(chip));
      const frame = document.createElement('i'), label = document.createElement('span');
      const source = chip.querySelector('img');
      if (source) {
        const image = document.createElement('img');
        image.src = source.src; image.width = 52; image.height = 52; image.alt = ''; image.loading = 'lazy';
        frame.append(image);
      } else frame.textContent = this.#name(chip);
      label.textContent = this.#name(chip);
      button.append(frame, label); strip.append(button);
    });
    this.#render();
  };

  #render() {
    const chip = this.#chips[this.#index], name = this.#name(chip);
    this.#element('title').textContent = name;
    this.#element('caption').textContent = name;
    this.#element('count').textContent = `${this.#index + 1} / ${this.#chips.length}`;
    this.#element('previous').disabled = this.#index === 0;
    this.#element('next').disabled = this.#index === this.#chips.length - 1;
    const select = this.#element('select');
    select.disabled = !this.#available(chip);
    select.textContent = select.disabled ? this.dataset.unavailable : this.dataset.select;
    for (const button of this.querySelectorAll('[data-colour-preview-index]')) {
      const current = Number(button.dataset.colourPreviewIndex) === this.#index;
      button.setAttribute('aria-current', String(current));
      if (current) button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }
    this.#loadImage(chip);
  }

  #loadImage(chip) {
    this.#releaseImage();
    const source = chip.querySelector('img'), src = source?.dataset.atelierFullSrc;
    const viewport = this.#element('image'), status = this.#element('status'), retry = this.#element('retry');
    retry.hidden = true;
    status.textContent = src ? this.dataset.loading : this.dataset.missing;
    viewport.setAttribute('aria-busy', String(!!src));
    if (!src) return;
    const wrapper = document.createElement('drag-zoom-wrapper'), image = document.createElement('img');
    wrapper.setAttribute('initial-zoom', '1'); wrapper.setAttribute('touch-enabled', '');
    image.setAttribute('ref', 'image'); image.alt = this.#name(chip); image.hidden = true;
    image.width = Number(source.dataset.atelierImageWidth); image.height = Number(source.dataset.atelierImageHeight);
    image.decoding = 'async'; this.#image = image;
    const finish = loaded => {
      if (this.#image !== image || !this.refs.dialog.open) return;
      image.onload = null; image.onerror = null;
      image.hidden = !loaded; status.textContent = loaded ? '' : this.dataset.failed;
      retry.hidden = loaded; viewport.setAttribute('aria-busy', 'false');
    };
    image.onload = () => finish(true); image.onerror = () => finish(false);
    wrapper.append(image); viewport.append(wrapper); image.src = src;
    if (image.complete) finish(image.naturalWidth > 0);
  }

  #releaseImage() {
    if (this.#image) {
      this.#image.onload = null; this.#image.onerror = null; this.#image.removeAttribute('src');
      this.#image = null;
    }
    this.#element('image').replaceChildren(); this.#swipe = null;
  }

  #step(delta) {
    const index = this.#index + delta;
    if (index < 0 || index >= this.#chips.length) return;
    this.#index = index; this.#render();
  }

  #onClick = event => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || button.disabled) return;
    if (button.hasAttribute('data-colour-preview-close')) this.closeDialog();
    else if (button.hasAttribute('data-colour-preview-next')) this.#step(1);
    else if (button.hasAttribute('data-colour-preview-previous')) this.#step(-1);
    else if (button.hasAttribute('data-colour-preview-retry')) this.#loadImage(this.#chips[this.#index]);
    else if (button.hasAttribute('data-colour-preview-index')) {
      this.#index = Number(button.dataset.colourPreviewIndex); this.#render();
    } else if (button.hasAttribute('data-colour-preview-select')) {
      const chip = this.#chips[this.#index];
      if (!this.#available(chip)) return;
      // The original chip owns variant, quantity, price and inquiry rules.
      chip.click(); this.closeDialog();
    }
  };

  #onKeyDown = event => {
    if (!this.refs.dialog.open || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    this.#step((event.key === 'ArrowRight' ? 1 : -1) * direction);
  };

  #touchStart = event => {
    if (event.touches.length !== 1 || this.#swipe) { this.#swipe = null; return; }
    const wrapper = this.#element('image').querySelector('drag-zoom-wrapper');
    if (!wrapper || wrapper.zoomScale > 1) return;
    const touch = event.touches[0];
    this.#swipe = { x: touch.clientX, y: touch.clientY };
  };

  #touchEnd = event => {
    const start = this.#swipe; this.#swipe = null;
    const wrapper = this.#element('image').querySelector('drag-zoom-wrapper');
    if (!start || event.touches.length || !wrapper || wrapper.zoomScale > 1) return;
    const touch = event.changedTouches[0], dx = touch.clientX - start.x, dy = touch.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    this.#step((dx < 0 ? 1 : -1) * direction);
  };

  #cancelSwipe = () => { this.#swipe = null; };
  #onClose = () => {
    this.#releaseImage(); this.#element('strip').replaceChildren();
    const target = this.#opener.disabled || this.#opener.hidden ? this.#selected() : this.#opener;
    if (target?.isConnected) target.focus({ preventScroll: true });
  };

  disconnectedCallback() {
    this.#listeners?.abort(); this.#releaseImage();
    this.#element('strip').replaceChildren();
    if (this.refs.dialog.open) this.refs.dialog.close();
    super.disconnectedCallback();
  }
}

if (!customElements.get('fivepoint-atelier-colour-preview')) customElements.define('fivepoint-atelier-colour-preview', AtelierColourPreview);
